import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Portal } from 'react-portal';

import {
  SR_API_ACTION_INSURANCE_ENABLE_FOR_PARTNER,
  SR_API_ACTION_INSURANCE_GET_PROPERTY_MANAGER_DETAILS,
  SR_API_ACTION_INSURANCE_UPDATE_PROPERTY_MANAGER_DETAILS,
} from 'api/utilityActionTypes';

import { stateOptionItems } from 'common/util/stateCodes';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_PARTNER_FORM_ADOBE } from 'client/components/SrFormikField';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

import {
  createFormValidationFn,
  validateFormFieldsAndSubmit,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import './UPEnableTenantLiabilityModal.less';

const getNameFields = ({ disabled }) => [
  {
    id: 'firstName',
    title: 'First Name',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter first name',
    },
    validations: ['required'],
  },
  {
    id: 'lastName',
    title: 'Last Name',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter last name',
    },
    validations: ['required'],
  },
];

const getContactFields = ({ disabled }) => [
  {
    id: 'phone',
    title: 'Phone Number',
    type: 'phone',
    properties: {
      disabled,
      placeholder: 'Enter phone number',
    },
    validations: ['required', 'phone'],
  },
  {
    id: 'email',
    title: 'Email',
    type: 'email',
    properties: {
      disabled,
      placeholder: 'Enter email address',
    },
    validations: ['required', 'email'],
  },
];

const getAddressFields = ({ disabled }) => [
  {
    id: 'legalCompanyName',
    title: 'Legal Company Name',
    type: 'short_text',
    properties: {
      disabled: true,
      placeholder: 'Enter legal company name',
    },
    validations: ['required'],
  },
  {
    id: 'addressLine1',
    title: 'Address Line 1',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter address line 1',
    },
    validations: ['required'],
  },
  {
    id: 'addressLine2',
    title: 'Address Line 2',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter address line 2 (optional)',
    },
    validations: [],
  },
];

const getCityStateZipFields = ({ disabled }) => [
  {
    id: 'city',
    title: 'City',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter city',
    },
    validations: ['required'],
  },
  {
    id: 'state',
    title: 'State',
    type: 'select',
    properties: {
      disabled,
      choices: stateOptionItems,
      placeholder: 'Select state',
    },
    validations: ['required'],
  },
  {
    id: 'zip',
    title: 'ZIP Code',
    type: 'short_text',
    properties: {
      disabled,
      placeholder: 'Enter ZIP code',
    },
    validations: ['required'],
  },
];

const UPEnableTenantLiabilityModal = ({ utilityPartner, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [existingData, setExistingData] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_INSURANCE_GET_PROPERTY_MANAGER_DETAILS,
      payload: {
        utilityPartnerId: utilityPartner.id,
      },
      onSuccess: ({ partnerInsuranceDetails }) => {
        setExistingData(partnerInsuranceDetails);
        setIsLoading(false);
      },
      onError: (error) => {
        // 404 means no existing data - this is expected for first-time enablement
        if (error.statusCode !== 404) {
          setFetchError(error);
        }
        setIsLoading(false);
      },
    });
  }, [dispatch, utilityPartner.id]);

  const sentinelRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(false);

  /*
   * Used to display a shadow effect when the user is at the top of the modal.
   * When the user starts scrolling and is about to reach the bottom, the shadow
   * disappear. It gives a visual impression that there's more content below.
   */
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAtBottom(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px -170px 0px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const nameFields = getNameFields({ disabled: isLoading });
  const contactFields = getContactFields({ disabled: isLoading });
  const addressFields = getAddressFields({ disabled: isLoading });
  const cityStateZipFields = getCityStateZipFields({ disabled: isLoading });
  const allFormFields = [...nameFields, ...contactFields, ...addressFields, ...cityStateZipFields];

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setSubmitError(null);

    const payload = {
      utilityPartnerId: utilityPartner.id,
      userFirstName: values.firstName,
      userLastName: values.lastName,
      legalPhone: values.phone,
      legalEmail: values.email,
      legalCompanyName: values.legalCompanyName,
      street1: values.addressLine1,
      street2: values.addressLine2,
      city: values.city,
      state: values.state,
      zip: values.zip,
    };

    // Map API field names to form field IDs
    const fieldNameMap = {
      userFirstName: 'firstName',
      userLastName: 'lastName',
      legalPhone: 'phone',
      legalEmail: 'email',
      legalCompanyName: 'legalCompanyName',
      street1: 'addressLine1',
      street2: 'addressLine2',
      city: 'city',
      state: 'state',
      zip: 'zip',
    };

    dispatch(requestUtilityApi, {
      action: existingData
        ? SR_API_ACTION_INSURANCE_UPDATE_PROPERTY_MANAGER_DETAILS
        : SR_API_ACTION_INSURANCE_ENABLE_FOR_PARTNER,
      payload,
      onSuccess: () => {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      },
      onError: (error) => {
        // Check if this is a typeCheck validation error with field info
        const errorDetails = error.inner?.details;
        if (errorDetails && errorDetails.varName) {
          const apiFieldName = errorDetails.varName;
          const formFieldId = fieldNameMap[apiFieldName] || apiFieldName;
          const errorMessage = error.inner?.message || error.message;

          // Set error on the specific field
          setFieldError(formFieldId, errorMessage);

          // Scroll to the field with error
          setTimeout(() => {
            scrollToFormField({ formFieldId });
          }, 100);
        } else {
          // If we can't determine the specific field, show general error
          setSubmitError(error.message || 'An error occurred while submitting the form');
        }

        setSubmitting(false);
      },
    });
  };

  const getInitialValues = () => {
    return {
      firstName: existingData?.userFirstName || '',
      lastName: existingData?.userLastName || '',
      phone: existingData?.legalPhone || '',
      email: existingData?.legalEmail || '',
      legalCompanyName: existingData?.legalCompanyName || utilityPartner.name || '',
      addressLine1: existingData?.street1 || '',
      addressLine2: existingData?.street2 || '',
      city: existingData?.city || '',
      state: existingData?.state || '',
      zip: existingData?.zip || '',
    };
  };

  if (fetchError) {
    return (
      <Portal>
        <SrBox
          alignItems="center"
          backgroundColor="rgba(25, 51, 64, 0.96)"
          bottom="0"
          display="flex"
          justifyContent="center"
          left="0"
          overflow="auto"
          position="fixed"
          right="0"
          top="0"
          zIndex="1000"
        >
          <SrBox
            alignItems="center"
            backgroundColor="white"
            borderRadius="14px"
            display="flex"
            flexDirection="column"
            gap="20px"
            padding="40px"
          >
            <SrText font="greycliff" color="red" weight="600" fontSize="18px">
              Failed to load data
            </SrText>
            <SrButton theme={SR_BUTTON_THEME_ADOBE_GREEN} onClick={onClose}>
              Close
            </SrButton>
          </SrBox>
        </SrBox>
      </Portal>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues()}
      onSubmit={handleSubmit}
      validate={(formValues) => createFormValidationFn(allFormFields)(formValues)}
    >
      {(formikBag) => (
        <Portal>
          <SrBox
            alignItems="flex-start"
            backgroundColor="rgba(25, 51, 64, 0.96)"
            bottom="0"
            display="flex"
            justifyContent="center"
            left="0"
            overflow="auto"
            padding="10vh 0"
            position="fixed"
            right="0"
            top="0"
            zIndex="1000"
          >
            <SrBox
              backgroundColor="#223C47"
              height="10vh"
              left="0"
              position="fixed"
              right="0"
              styleName="topOverlayMask"
              top="0"
              zIndex="10"
            />
            <SrBox
              backgroundColor="#223C47"
              bottom="0"
              height="10vh"
              left="0"
              position="fixed"
              right="0"
              styleName="bottomOverlayMask"
              zIndex="10"
            />
            <SrBox
              backgroundColor="white"
              borderRadius="14px"
              display="flex"
              flexDirection="column"
              maxWidth="600px"
              position="relative"
              width="100%"
            >
              {/* Header */}
              <SrBox
                alignItems="center"
                backgroundColor="white"
                borderRadius="14px 14px 0 0"
                display="flex"
                justifyContent="space-between"
                padding="40px 40px 24px"
                position="sticky"
                top="0"
                zIndex="1"
              >
                <SrFlex align="center" gap="12px">
                  <SrBox
                    backgroundImage="url(/images/simplecover-shield-icon.png)"
                    backgroundPosition="center"
                    backgroundRepeat="no-repeat"
                    backgroundSize="contain"
                    flexShrink="0"
                    height="32px"
                    width="32px"
                  />
                  <SrText font="greycliff" color="blueGreycliff" weight="600" fontSize="26px">
                    Enable Tenant Liability Protection
                  </SrText>
                </SrFlex>
                <button
                  aria-label="Close modal"
                  onClick={onClose}
                  styleName="closeButton"
                  type="button"
                >
                  &times;
                </button>
              </SrBox>

              {/* Content */}
              <SrBox backgroundColor="white" padding="0px 40px 0px">
                <Form className="form">
                  <SrFlex
                    align="center"
                    justify="between"
                    marginBottom="16px"
                    paddingBottom="8px"
                    paddingRight="8px"
                  >
                    <SrText font="greycliff" color="blueGreycliff" weight="700" fontSize="18px">
                      User Information
                    </SrText>
                    {isLoading && (
                      <SrFlex align="center" gap="4px">
                        <SrText
                          color="greyMonochromeInactive"
                          font="greycliff"
                          fontSize="14px"
                          weight="500"
                        >
                          Fetching details
                        </SrText>
                        <SrFlex align="center" gap="4px" marginLeft="2px">
                          <span styleName="dot" />
                          <span styleName="dot" />
                          <span styleName="dot" />
                        </SrFlex>
                      </SrFlex>
                    )}
                  </SrFlex>
                  <SrFlex gap="16px" marginBottom="16px">
                    {nameFields.map((formField) => (
                      <SrBox key={formField.id} flex="1">
                        <SrFormikField
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                        />
                      </SrBox>
                    ))}
                  </SrFlex>
                  {contactFields.map((formField) => (
                    <SrBox key={formField.id} marginBottom="16px">
                      <SrFormikField
                        formField={formField}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                      />
                    </SrBox>
                  ))}
                  <SrBox height="1px" backgroundColor="#e8e8e8" margin="24px 0" />
                  <SrFlex
                    align="center"
                    justify="between"
                    marginBottom="16px"
                    paddingBottom="8px"
                    paddingRight="8px"
                  >
                    <SrText font="greycliff" color="blueGreycliff" weight="700" fontSize="18px">
                      Company Information
                    </SrText>
                  </SrFlex>
                  {addressFields.map((formField) => (
                    <SrBox key={formField.id} marginBottom="16px">
                      <SrFormikField
                        formField={formField}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                      />
                    </SrBox>
                  ))}
                  <SrFlex gap="16px" marginBottom="16px">
                    {cityStateZipFields.map((formField) => (
                      <SrBox key={formField.id} flex="1">
                        <SrFormikField
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                        />
                      </SrBox>
                    ))}
                  </SrFlex>
                  <div ref={sentinelRef} />
                </Form>
              </SrBox>

              {/* Footer */}
              <SrBox
                backgroundColor="white"
                borderRadius="0 0 14px 14px"
                bottom="0"
                padding="16px 40px 40px"
                position="sticky"
                zIndex="1"
              >
                <SrBox
                  backdropFilter="blur(1px) saturate(100%)"
                  background="linear-gradient(to top, rgba(255,255,255,0.7), rgba(255,255,255,0))"
                  height="120px"
                  left="0"
                  pointerEvents="none"
                  position="absolute"
                  right="0"
                  top="-120px"
                  visibility={isAtBottom ? 'hidden' : 'visible'}
                  zIndex="1"
                />
                {submitError && (
                  <SrBox marginBottom="16px">
                    <SrErrorMessage>{submitError}</SrErrorMessage>
                  </SrBox>
                )}
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  width="100%"
                  disabled={isLoading || formikBag.isSubmitting}
                  onClick={() =>
                    validateFormFieldsAndSubmit({
                      formikBag,
                      formFields: allFormFields,
                    })
                  }
                >
                  Continue
                </SrButton>
              </SrBox>
            </SrBox>
          </SrBox>
        </Portal>
      )}
    </Formik>
  );
};

UPEnableTenantLiabilityModal.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

UPEnableTenantLiabilityModal.displayName = 'UPEnableTenantLiabilityModal';

export default UPEnableTenantLiabilityModal;
